import { useState } from "react";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import axios from "axios";
import {
  TrendingUp, Search, Shield, CheckCircle, AlertTriangle, XCircle,
  Cookie, Globe, FileText, Lock, Eye, AlertCircle, Loader2, Download
} from "lucide-react";

const CHECKS = [
  { id: "cookie_banner", icon: Cookie, weight: 15 },
  { id: "consent_mechanism", icon: CheckCircle, weight: 15 },
  { id: "cookie_policy", icon: FileText, weight: 10 },
  { id: "privacy_policy", icon: Lock, weight: 10 },
  { id: "third_party_cookies", icon: Eye, weight: 10 },
  { id: "analytics_consent", icon: Globe, weight: 10 },
  { id: "marketing_consent", icon: AlertTriangle, weight: 10 },
  { id: "data_retention", icon: Shield, weight: 5 },
  { id: "cross_border", icon: Globe, weight: 5 },
  { id: "opt_out_mechanism", icon: XCircle, weight: 10 },
];

export default function CookieAuditPage() {
  const { t } = useLanguage();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const handleScan = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError("");
    setResults(null);
    try {
      const res = await axios.post(`${API}/cookie-audit`, { url: url.trim() });
      setResults(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Scan failed. Please try again.");
    }
    setLoading(false);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-emerald-400";
    if (score >= 50) return "text-[#FFE600]";
    return "text-[#a855f7]";
  };

  const getStatusIcon = (status) => {
    if (status === "pass") return <CheckCircle className="w-4 h-4 text-emerald-400" />;
    if (status === "warn") return <AlertTriangle className="w-4 h-4 text-[#FFE600]" />;
    return <XCircle className="w-4 h-4 text-[#a855f7]" />;
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_home || "Home"}</a>
            <a href="/plugin" className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] font-['JetBrains_Mono'] uppercase">{t.nav_plugin || "Plugin"}</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 text-blue-400 font-['JetBrains_Mono'] text-sm tracking-[0.2em] uppercase mb-6">
            <Shield className="w-4 h-4" /> {t.ca_badge || "Free GDPR Tool"}
          </span>
          <h1 className="text-5xl sm:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.9] mb-6">
            {t.ca_h1_1 || "ONE-CLICK"}<br />
            <span className="text-blue-400">{t.ca_h1_2 || "COOKIE & GDPR"}</span><br />
            {t.ca_h1_3 || "AUDIT"}
          </h1>
          <p className="text-xl text-[var(--color-text-muted)] mb-10 max-w-2xl mx-auto">
            {t.ca_desc || "Check if your website is GDPR, CCPA and ePrivacy compliant. Get a detailed cookie compliance report with actionable fixes in 30 seconds."}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto mb-4">
            <input
              data-testid="cookie-url-input"
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleScan(); }}
              placeholder={t.ca_placeholder || "Enter your website URL..."}
              className="flex-1 px-6 py-4 bg-[var(--color-surface)] border-2 border-[var(--color-border)] text-white text-lg font-['JetBrains_Mono'] focus:border-blue-500 focus:outline-none placeholder:text-[var(--color-text-muted)]"
            />
            <button
              data-testid="cookie-scan-btn"
              onClick={handleScan}
              disabled={loading || !url.trim()}
              className="px-8 py-4 bg-blue-600 text-white font-black text-lg uppercase tracking-wider hover:bg-blue-500 transition-all disabled:opacity-50 flex items-center gap-3"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              {loading ? (t.ca_scanning || "SCANNING...") : (t.ca_scan || "AUDIT NOW")}
            </button>
          </div>
          <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono']">{t.ca_free || "Free. No registration. Instant results."}</p>
          {error && <div className="mt-4 text-[#a855f7] text-sm flex items-center justify-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
        </div>
      </section>

      {/* Results */}
      {results && (
        <section className="py-12 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]" data-testid="cookie-results">
          <div className="max-w-4xl mx-auto">
            {/* Score */}
            <div className="text-center mb-12">
              <p className="text-[var(--color-text-muted)] text-sm font-['JetBrains_Mono'] uppercase mb-2">{t.ca_score_label || "Cookie Compliance Score"}</p>
              <p className={`text-8xl font-black font-['Cabinet_Grotesk'] ${getScoreColor(results.score)}`}>{results.score}<span className="text-3xl text-[var(--color-text-muted)]">/100</span></p>
              <p className="text-[var(--color-text-muted)] mt-2 text-lg">{results.url}</p>
              <div className="flex justify-center gap-4 mt-4">
                <span className={`text-xs px-3 py-1 font-bold font-['JetBrains_Mono'] uppercase ${results.gdpr_compliant ? 'bg-emerald-900/30 text-emerald-400' : 'bg-red-900/30 text-[#a855f7]'}`}>
                  GDPR: {results.gdpr_compliant ? (t.ca_pass || "PASS") : (t.ca_fail || "FAIL")}
                </span>
                <span className={`text-xs px-3 py-1 font-bold font-['JetBrains_Mono'] uppercase ${results.ccpa_compliant ? 'bg-emerald-900/30 text-emerald-400' : 'bg-red-900/30 text-[#a855f7]'}`}>
                  CCPA: {results.ccpa_compliant ? (t.ca_pass || "PASS") : (t.ca_fail || "FAIL")}
                </span>
                <span className={`text-xs px-3 py-1 font-bold font-['JetBrains_Mono'] uppercase ${results.eprivacy_compliant ? 'bg-emerald-900/30 text-emerald-400' : 'bg-amber-900/30 text-[#FFE600]'}`}>
                  ePrivacy: {results.eprivacy_compliant ? (t.ca_pass || "PASS") : (t.ca_warn || "WARN")}
                </span>
              </div>
            </div>

            {/* Checks */}
            <div className="space-y-3 mb-8">
              <h3 className="text-lg font-bold font-['Cabinet_Grotesk'] mb-4">{t.ca_checks || "Detailed Checks"}</h3>
              {(results.checks || []).map((check, i) => (
                <div key={i} className="bg-[var(--color-bg)] border border-[var(--color-border)] p-4 flex items-start gap-4" data-testid={`check-${check.id}`}>
                  {getStatusIcon(check.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-white">{check.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 font-bold uppercase ${
                        check.status === 'pass' ? 'bg-emerald-900/30 text-emerald-400' :
                        check.status === 'warn' ? 'bg-amber-900/30 text-[#FFE600]' :
                        'bg-red-900/30 text-[#a855f7]'
                      }`}>{check.status === 'pass' ? (t.ca_pass || 'PASS') : check.status === 'warn' ? (t.ca_warn || 'WARN') : (t.ca_fail || 'FAIL')}</span>
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">{check.detail}</p>
                    {check.fix && <p className="text-xs text-blue-400 mt-1 font-['JetBrains_Mono']">{t.ca_fix || "Fix"}: {check.fix}</p>}
                  </div>
                </div>
              ))}
            </div>

            {/* Cookies found */}
            {results.cookies_found && results.cookies_found.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold font-['Cabinet_Grotesk'] mb-4">{t.ca_cookies_found || "Cookies Found"} ({results.cookies_found.length})</h3>
                <div className="bg-black border border-[var(--color-border)] overflow-x-auto">
                  <table className="w-full text-xs font-['JetBrains_Mono']">
                    <thead className="bg-[var(--color-surface)] text-[var(--color-text-muted)]">
                      <tr>
                        <th className="text-left px-4 py-2">{t.ca_cookie_name || "Cookie"}</th>
                        <th className="text-left px-4 py-2">{t.ca_cookie_type || "Type"}</th>
                        <th className="text-left px-4 py-2">{t.ca_cookie_domain || "Domain"}</th>
                        <th className="text-left px-4 py-2">{t.ca_cookie_exp || "Expiry"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.cookies_found.map((c, i) => (
                        <tr key={i} className="border-t border-[var(--color-border)]">
                          <td className="px-4 py-2 text-white">{c.name}</td>
                          <td className="px-4 py-2"><span className={`px-1.5 py-0.5 text-[10px] ${c.type === 'necessary' ? 'bg-emerald-900/30 text-emerald-400' : c.type === 'analytics' ? 'bg-blue-900/30 text-blue-400' : 'bg-red-900/30 text-[#a855f7]'}`}>{c.type}</span></td>
                          <td className="px-4 py-2 text-[var(--color-text-muted)]">{c.domain}</td>
                          <td className="px-4 py-2 text-[var(--color-text-muted)]">{c.expiry}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="text-center mt-8 p-8 bg-[var(--color-bg)] border border-[var(--color-border)]">
              <p className="text-[var(--color-text-muted)] mb-4">{t.ca_cta_text || "Want a full SEO + AI Visibility audit?"}</p>
              <a href="/" className="inline-flex items-center gap-3 px-8 py-4 bg-[#a855f7] text-white font-black text-lg uppercase tracking-wider hover:bg-purple-600 transition-all" data-testid="full-audit-cta">
                <Search className="w-5 h-5" /> {t.ca_cta_btn || "FREE SEO SCAN"}
              </a>
            </div>
          </div>
        </section>
      )}

      {/* What we check */}
      {!results && (
        <section className="py-20 px-6 bg-[var(--color-surface)] border-y border-[var(--color-border)]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-12">
              {t.ca_what || "WHAT WE"} <span className="text-blue-400">{t.ca_what2 || "CHECK"}</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { icon: Cookie, title: t.ca_c1 || "Cookie Banner", desc: t.ca_c1d || "Does your site show a proper GDPR cookie consent banner before setting non-essential cookies?" },
                { icon: CheckCircle, title: t.ca_c2 || "Consent Mechanism", desc: t.ca_c2d || "Can users explicitly opt-in/opt-out of different cookie categories (analytics, marketing, preferences)?" },
                { icon: FileText, title: t.ca_c3 || "Cookie Policy", desc: t.ca_c3d || "Do you have a detailed cookie policy explaining what cookies you use and why?" },
                { icon: Lock, title: t.ca_c4 || "Privacy Policy", desc: t.ca_c4d || "Is there a comprehensive privacy policy linked from the cookie banner?" },
                { icon: Eye, title: t.ca_c5 || "Third-Party Cookies", desc: t.ca_c5d || "Are third-party cookies (Google Analytics, Facebook Pixel, etc.) loaded before consent?" },
                { icon: Globe, title: t.ca_c6 || "Cross-Border Compliance", desc: t.ca_c6d || "GDPR (EU), CCPA (California), LGPD (Brazil), POPIA (South Africa) compliance check." },
              ].map((f, i) => (
                <div key={i} className="bg-[var(--color-bg)] border border-[var(--color-border)] p-6 flex items-start gap-4 hover:border-blue-500/30 transition-colors" data-testid={`check-info-${i}`}>
                  <f.icon className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-white font-['Cabinet_Grotesk']">{f.title}</h3>
                    <p className="text-[var(--color-text-muted)] text-sm mt-1">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="py-8 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-4 h-4 text-white" /></div>
            <span className="font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
          </div>
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">© 2026 TopRankPulse</p>
        </div>
      </footer>
    </div>
  );
}
